import type { IImage, ITrack, ITrackItem, IVideo } from "@designcombo/types";
import type { BRollItem, ShortWithRelations } from "@repo/types";
import { manageKenBurnsEffect } from "../animation/ken-burns";
import { mediaInAnimationPresetList } from "../animation/media-in-presets";
import { mediaOutAnimationPresetList } from "../animation/media-out-presets";
import { configs } from "../configs";
import { generateId } from "../trackId";
import { mergeAdjacentBRollClipTimings } from "./broll-clip-timing-merge";
import {
	computeUniformCoverLayout,
	createImageTrackItem,
	getImageDimensionsForWidth,
} from "./image-mapper";
import { createVideoDetails } from "./video-mapper";

/** Prefix for B-roll track and clip names so they are easy to spot next to captions. */
export const BROLL_TRACK_NAME_PREFIX = "@broll";

export interface BRollLayersResult {
	trackItems: ITrackItem[];
	tracks: ITrack[];
}

const BROLL_ALLOWED_IMAGE_IN_ANIMATION_NAMES = [
	// "fadeIn",
	// "scaleIn",
	"kenBurnsIn",
] as const;
const BROLL_ALLOWED_VIDEO_IN_ANIMATION_NAMES = ["fadeIn", "scaleIn"] as const;
const BROLL_ALLOWED_IMAGE_OUT_ANIMATION_NAMES = ["fadeOut", "scaleOut"] as const;
const BROLL_ALLOWED_VIDEO_OUT_ANIMATION_NAMES = ["fadeOut", "scaleOut"] as const;
const BROLL_ANIMATION_RULES = {
	/**
	 * When one of these "in" animations is selected, disable "out" to avoid clashing motion.
	 * Useful for long in-effects like Ken Burns.
	 */
	disableOutWhenInAnimationNames: ["kenBurnsIn"] as const,
} as const;

function getHashNumber(value: string): number {
	let hash = 0;
	for (let i = 0; i < value.length; i++) {
		hash = (hash * 31 + value.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
}

function getAllowedInAnimationPresets(
	allowedNames: readonly string[],
) {
	const allowed = mediaInAnimationPresetList.filter((preset) =>
		allowedNames.includes(preset.name),
	);
	return allowed.length > 0 ? allowed : mediaInAnimationPresetList;
}

function getAllowedOutAnimationPresets(
	allowedNames: readonly string[],
) {
	const allowed = mediaOutAnimationPresetList.filter((preset) =>
		allowedNames.includes(preset.name),
	);
	return allowed.length > 0 ? allowed : mediaOutAnimationPresetList;
}

function getRandomInAnimationForBRoll(
	bRollId: string,
	allowedNames: readonly string[],
) {
	const allowedIn = getAllowedInAnimationPresets(allowedNames);
	const idx = getHashNumber(bRollId) % allowedIn.length;
	return allowedIn[idx];
}

function getRandomOutAnimationForBRoll(
	bRollId: string,
	allowedNames: readonly string[],
) {
	const allowedOut = getAllowedOutAnimationPresets(allowedNames);
	const idx = getHashNumber(`${bRollId}-out`) % allowedOut.length;
	return allowedOut[idx];
}

interface BRollAnimationSelection {
	in?: {
		name: string;
		composition: Array<Record<string, unknown>>;
	};
	out?: {
		name: string;
		composition: Array<Record<string, unknown>>;
	};
}

function getBRollAnimations(
	bRoll: BRollItem,
	options?: { disableIn?: boolean; disableOut?: boolean },
): BRollAnimationSelection {
	// Keep this in one place so future conditional logic (plan, feature flags, clip length, etc.)
	// can decide whether to add in/out animations and which preset family to use.
	const isImage = bRoll.fileType === "image";
	const inAnimationPreset = isImage
		? getRandomInAnimationForBRoll(bRoll.id, BROLL_ALLOWED_IMAGE_IN_ANIMATION_NAMES)
		: getRandomInAnimationForBRoll(bRoll.id, BROLL_ALLOWED_VIDEO_IN_ANIMATION_NAMES);
	const outAnimationPreset = isImage
		? getRandomOutAnimationForBRoll(bRoll.id, BROLL_ALLOWED_IMAGE_OUT_ANIMATION_NAMES)
		: getRandomOutAnimationForBRoll(bRoll.id, BROLL_ALLOWED_VIDEO_OUT_ANIMATION_NAMES);

	let inComposition: Array<Record<string, unknown>> = [];
	let inName = inAnimationPreset.name;
	if (isImage && inAnimationPreset.name === "kenBurnsIn") {
		const kenBurns = manageKenBurnsEffect({ seed: bRoll.id });
		inName = kenBurns.presetName;
		inComposition = kenBurns.composition.map((step) => ({ ...step }));
	} else {
		inComposition = inAnimationPreset.composition.map((step) => ({
			...step,
		}));
	}

	const animationSelection: BRollAnimationSelection = {};
	if (!options?.disableIn) {
		animationSelection.in = {
			name: inName,
			composition: inComposition,
		};
	}
	const shouldDisableOutFromRule = BROLL_ANIMATION_RULES.disableOutWhenInAnimationNames.includes(
		inAnimationPreset.name as (typeof BROLL_ANIMATION_RULES.disableOutWhenInAnimationNames)[number],
	);
	if (!options?.disableOut && !shouldDisableOutFromRule) {
		animationSelection.out = {
			name: outAnimationPreset.name,
			composition: outAnimationPreset.composition.map((step) => ({ ...step })),
		};
	}
	return animationSelection;
}

interface IntrinsicDimensions {
	width: number;
	height: number;
}

/**
 * Reads pixel dimensions from B-roll metadata (set by generateBRoll from Pexels asset width/height).
 */
function readIntrinsicDimensionsFromMetadata(
	bRoll: BRollItem,
): IntrinsicDimensions | null {
	const meta = bRoll.metadata;
	if (!meta || typeof meta !== "object") {
		return null;
	}
	const m = meta as Record<string, unknown>;
	const rawW = m.intrinsicWidth ?? m.width;
	const rawH = m.intrinsicHeight ?? m.height;
	const w = typeof rawW === "number" ? rawW : Number(rawW);
	const h = typeof rawH === "number" ? rawH : Number(rawH);
	if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
		return { width: w, height: h };
	}
	return null;
}

/**
 * Resolves intrinsic pixel size: metadata from Pexels (preferred), HTTP probe for legacy image B-rolls,
 * or canvas size so layout falls back to full-frame (same as pre-metadata shorts).
 */
async function resolveIntrinsicDimensions(
	bRoll: BRollItem,
	resolution: { width: number; height: number },
): Promise<IntrinsicDimensions> {
	const fromMeta = readIntrinsicDimensionsFromMetadata(bRoll);
	if (fromMeta) {
		return fromMeta;
	}
	if (bRoll.fileType === "image" && bRoll.fileUrl) {
		try {
			const dims = await getImageDimensionsForWidth(bRoll.fileUrl);
			return { width: dims.originalWidth, height: dims.originalHeight };
		} catch {
			// probe failed (network, unsupported URL, etc.)
		}
	}
	return { width: resolution.width, height: resolution.height };
}

/**
 * Watermark-style: keep native width/height on the item, use uniform scale on `details.transform`,
 * and center the unscaled box in the composition (transform-origin center).
 */
function centerUnscaledBoxInCanvas(
	canvasW: number,
	canvasH: number,
	boxW: number,
	boxH: number,
): { top: string; left: string } {
	const left = Math.round((canvasW - boxW) / 2);
	const top = Math.round((canvasH - boxH) / 2);
	return { left: `${left}px`, top: `${top}px` };
}

function getBRollsFromShort(short: ShortWithRelations): BRollItem[] {
	const withRolls = short as ShortWithRelations & { bRolls?: BRollItem[] };
	const raw = withRolls.bRolls;
	return Array.isArray(raw) ? raw : [];
}

function snapAdjacentBrollBoundaries(
	brolls: BRollItem[],
	maxGapSec: number,
): BRollItem[] {
	if (brolls.length <= 1) return brolls.map((b) => ({ ...b }));

	const sorted = [...brolls]
		.map((b) => ({ ...b }))
		.sort((a, b) => a.startTime - b.startTime);

	for (let i = 0; i < sorted.length - 1; i++) {
		const current = sorted[i];
		const next = sorted[i + 1];
		const gap = next.startTime - current.endTime;
		if (gap >= 0 && gap <= maxGapSec) current.endTime = next.startTime;
	}

	return sorted;
}

/**
 * Maps `short.bRolls` into editor track items and one track per media type (video / image),
 * same pattern as captions: a dedicated row with prefixed names.
 *
 * Sizing: intrinsic dimensions on the layer, uniform **cover** scale ({@link computeUniformCoverLayout})
 * so B-roll fills the composition (like `object-fit: cover`), centered in the scene.
 */
export async function createBRollLayersFromShort(
	short: ShortWithRelations,
	resolution: { width: number; height: number },
): Promise<BRollLayersResult> {
	const rawRolls = getBRollsFromShort(short).filter((b) => Boolean(b?.fileUrl));
	if (rawRolls.length === 0) {
		return { trackItems: [], tracks: [] };
	}

	const bRolls = mergeAdjacentBRollClipTimings(
		rawRolls,
		configs.bRoll.adjacentClipMergeMaxGapSec,
		undefined,
		configs.bRoll.adjacentClipSnapToNextStart,
	);
	const animationBoundaryGapSec = Math.max(
		0,
		configs.bRoll.animationBoundaryGapSec,
	);
	const sorted = snapAdjacentBrollBoundaries(
		bRolls,
		animationBoundaryGapSec,
	);
	const videoClipItems: IVideo[] = [];
	const imageClipItems: IImage[] = [];

	for (let index = 0; index < sorted.length; index++) {
		const bRoll = sorted[index];
		const previous = index > 0 ? sorted[index - 1] : null;
		const next = index < sorted.length - 1 ? sorted[index + 1] : null;
		const gapFromPrevious =
			previous ? Math.max(0, bRoll.startTime - previous.endTime) : Number.POSITIVE_INFINITY;
		const gapToNext =
			next ? Math.max(0, next.startTime - bRoll.endTime) : Number.POSITIVE_INFINITY;
		const disableIn = gapFromPrevious <= animationBoundaryGapSec;
		const disableOut = gapToNext <= animationBoundaryGapSec;
		const startMs = Math.max(0, bRoll.startTime * 1000);
		const endMs = Math.max(startMs + 1, bRoll.endTime * 1000);
		const durationMs = endMs - startMs;
		const animations = getBRollAnimations(bRoll, { disableIn, disableOut });
		const clipName = `${BROLL_TRACK_NAME_PREFIX}/clip-${index + 1}`;

		const intrinsic = await resolveIntrinsicDimensions(bRoll, resolution);
		const cover = computeUniformCoverLayout(
			resolution.width,
			resolution.height,
			intrinsic.width,
			intrinsic.height,
		);
		const scaleStr = String(cover.scale);
		const position = centerUnscaledBoxInCanvas(
			resolution.width,
			resolution.height,
			intrinsic.width,
			intrinsic.height,
		);

		if (bRoll.fileType === "video") {
			const details = createVideoDetails({
				resolution: { width: intrinsic.width, height: intrinsic.height },
				src: bRoll.fileUrl,
				zIndex: 1,
				volume: 0,
				overrides: {
					top: position.top,
					left: position.left,
					transform: `scale(${scaleStr})`,
					transformOrigin: "center center",
				},
			});

			const trackItem = {
				id: generateId(10),
				name: clipName,
				type: "video",
				display: {
					from: startMs,
					to: endMs,
				},
				trim: {
					from: 0,
					to: durationMs,
				},
				details,
				metadata: {
					...(bRoll.metadata && typeof bRoll.metadata === "object"
						? (bRoll.metadata as Record<string, unknown>)
						: {}),
					bRollId: bRoll.id,
					sentenceIds: bRoll.sentenceIds,
					pexelsId: bRoll.pexelsId,
				},
				duration: durationMs,
				playbackRate: 1,
				isMain: false,
			} as IVideo;
			const resolvedAnimations: Record<string, unknown> = {};
			if (animations.in) {
				resolvedAnimations.in = {
					name: animations.in.name,
					composition: animations.in.composition,
				};
			}
			if (animations.out) {
				resolvedAnimations.out = {
					name: animations.out.name,
					composition: animations.out.composition,
				};
			}
			if (Object.keys(resolvedAnimations).length > 0)
				trackItem.animations = resolvedAnimations as any;

			videoClipItems.push(trackItem);
			continue;
		}

		const imageItem = createImageTrackItem({
			src: bRoll.fileUrl,
			width: intrinsic.width,
			height: intrinsic.height,
			zIndex: 1,
			top: position.top,
			left: position.left,
			overrides: {
				transform: `scale(${scaleStr})`,
				// Keep base cover transform centered. Random transform-origin on base scale can push
				// media out of frame at t=0; Ken Burns dynamics come from animated translate values.
				transformOrigin: "center center",
			},
			display: {
				from: startMs,
				to: endMs,
			},
			duration: durationMs,
			name: clipName,
			metadata: {
				...(bRoll.metadata && typeof bRoll.metadata === "object"
					? (bRoll.metadata as Record<string, unknown>)
					: {}),
				bRollId: bRoll.id,
				sentenceIds: bRoll.sentenceIds,
				pexelsId: bRoll.pexelsId,
			},
		});
		const resolvedAnimations: Record<string, unknown> = {};
		if (animations.in) {
			resolvedAnimations.in = {
				name: animations.in.name,
				composition: animations.in.composition,
			};
		}
		if (animations.out) {
			resolvedAnimations.out = {
				name: animations.out.name,
				composition: animations.out.composition,
			};
		}
		if (Object.keys(resolvedAnimations).length > 0)
			imageItem.animations = resolvedAnimations as any;

		imageClipItems.push(imageItem);
	}

	const trackItems: ITrackItem[] = [...videoClipItems, ...imageClipItems];
	const tracks: ITrack[] = [];

	if (videoClipItems.length > 0) {
		tracks.push({
			id: generateId(10),
			name: `${BROLL_TRACK_NAME_PREFIX} · video`,
			type: "video",
			items: videoClipItems.map((v) => v.id),
			accepts: ["video"],
		} as ITrack);
	}

	if (imageClipItems.length > 0) {
		tracks.push({
			id: generateId(10),
			name: `${BROLL_TRACK_NAME_PREFIX} · image`,
			type: "image",
			items: imageClipItems.map((i) => i.id),
			accepts: ["image"],
		} as ITrack);
	}

	return { trackItems, tracks };
}
